import React, {Fragment, useCallback, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {
    Ban,
    CheckCircle,
    ChevronDown,
    ChevronUp,
    Loader2,
    Pencil,
    Plus,
    Search,
    Tag,
} from 'lucide-react';
import clsx from 'clsx';
import {toast} from 'react-toastify';
import AdminService from '@/modules/admin/services/adminService';
import type {AdminCoupon, AdminCouponInput, CouponDiscountType} from '@/shared/types/api';

const PAGE_SIZE = 50;
const DISCOUNT_TYPES: CouponDiscountType[] = ['percentage', 'dollars'];
type SortCol = 'code' | 'created_at' | 'discount_amount' | 'points_cost' | 'used_on';

interface Props {
    orgId?: number;
}

// ── Helpers ──────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined) {
    if (!iso) return <span className="text-mld-muted-text">&mdash;</span>;
    return new Date(iso).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
}

function fmtDiscount(coupon: AdminCoupon) {
    if (coupon.discountType === 'percentage') return `${coupon.discountAmount}%`;
    return `$${Number(coupon.discountAmount).toFixed(2)}`;
}

function ValidBadge({valid}: {valid: boolean}) {
    return (
        <span
            className={clsx(
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                valid
                    ? 'bg-mld-status-normal/10 text-mld-status-normal'
                    : 'bg-gray-100 text-mld-muted-text line-through'
            )}>
            {valid ? 'Active' : 'Invalidated'}
        </span>
    );
}

// ── Main component ────────────────────────────────────────────────────

const AdminCouponsManager: React.FC<Props> = ({orgId}) => {
    const queryClient = useQueryClient();

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filterValid, setFilterValid] = useState<'all' | 'true' | 'false'>('all');
    const [sortCol, setSortCol] = useState<SortCol>('created_at');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [offset, setOffset] = useState(0);

    const [createOpen, setCreateOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<AdminCoupon | null>(null);
    const [toggleTarget, setToggleTarget] = useState<AdminCoupon | null>(null);

    // Debounce search input
    const handleSearchChange = useCallback((val: string) => {
        setSearch(val);
        clearTimeout((handleSearchChange as any)._t);
        (handleSearchChange as any)._t = setTimeout(() => {
            setDebouncedSearch(val);
            setOffset(0);
        }, 350);
    }, []);

    const queryKey = ['admin', 'coupons', orgId, debouncedSearch, filterValid, sortCol, sortDir, offset];

    const {data, isLoading} = useQuery({
        queryKey,
        queryFn: () =>
            AdminService.listCoupons({
                organizationId: orgId,
                search: debouncedSearch || undefined,
                valid: filterValid === 'all' ? undefined : filterValid === 'true',
                limit: PAGE_SIZE,
                offset,
                sortBy: sortCol,
                sortDir,
            }),
        staleTime: 30_000,
    });

    const toggleMutation = useMutation({
        mutationFn: ({id, valid}: {id: number; valid: boolean}) =>
            AdminService.updateCoupon(id, {valid}),
        onSuccess: (_, {valid}) => {
            queryClient.invalidateQueries({queryKey: ['admin', 'coupons']});
            toast.success(valid ? 'Coupon activated' : 'Coupon invalidated');
            setToggleTarget(null);
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.error ?? 'Failed to update coupon');
        },
    });

    const rows = data?.rows ?? [];
    const total = data?.total ?? 0;

    const handleSort = (col: SortCol) => {
        if (col === sortCol) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortCol(col);
            setSortDir('asc');
        }
        setOffset(0);
    };

    const SortIcon = ({col}: {col: SortCol}) => {
        if (col !== sortCol) return null;
        return sortDir === 'asc' ? (
            <ChevronUp className="inline h-3 w-3 ml-0.5" />
        ) : (
            <ChevronDown className="inline h-3 w-3 ml-0.5" />
        );
    };

    const thCls = (col: SortCol) =>
        clsx(
            'px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none',
            col === sortCol ? 'text-mld-primary' : 'text-mld-muted-text'
        );

    return (
        <div className="mt-4">
            {/* Toolbar */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[180px] max-w-xs">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-mld-muted-text" />
                    <input
                        type="text"
                        placeholder="Search coupons…"
                        value={search}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="w-full rounded-md border border-gray-300 pl-8 pr-3 py-1.5 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                    />
                </div>

                <select
                    value={filterValid}
                    onChange={(e) => {
                        setFilterValid(e.target.value as any);
                        setOffset(0);
                    }}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary">
                    <option value="all">All status</option>
                    <option value="true">Active only</option>
                    <option value="false">Invalidated only</option>
                </select>

                <button
                    type="button"
                    onClick={() => setCreateOpen(true)}
                    className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-mld-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-mld-primary-700">
                    <Plus className="h-4 w-4" />
                    New Coupon
                </button>
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-5 w-5 animate-spin text-mld-muted-text" />
                </div>
            ) : rows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-mld-panel-border py-14 text-center">
                    <Tag className="mx-auto mb-2 h-8 w-8 text-mld-muted-text" />
                    <p className="text-sm text-mld-muted-text">No coupons found.</p>
                </div>
            ) : (
                <div className="overflow-hidden rounded-lg border border-mld-panel-border">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-mld-panel-border bg-mld-menu-background">
                                <th className={thCls('code')} onClick={() => handleSort('code')}>
                                    Code <SortIcon col="code" />
                                </th>
                                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                    User
                                </th>
                                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                    Organization
                                </th>
                                <th
                                    className={thCls('discount_amount')}
                                    onClick={() => handleSort('discount_amount')}>
                                    Discount <SortIcon col="discount_amount" />
                                </th>
                                <th
                                    className={thCls('points_cost')}
                                    onClick={() => handleSort('points_cost')}>
                                    Pts Cost <SortIcon col="points_cost" />
                                </th>
                                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                    Status
                                </th>
                                <th
                                    className={thCls('used_on')}
                                    onClick={() => handleSort('used_on')}>
                                    Used On <SortIcon col="used_on" />
                                </th>
                                <th
                                    className={thCls('created_at')}
                                    onClick={() => handleSort('created_at')}>
                                    Created <SortIcon col="created_at" />
                                </th>
                                <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((coupon) => (
                                <tr
                                    key={coupon.id}
                                    className="border-b border-mld-panel-border/30 last:border-0 hover:bg-gray-50">
                                    <td className="px-4 py-2.5 font-mono text-xs font-medium text-mld-text-primary">
                                        {coupon.code}
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-mld-text-secondary">
                                        <div>{coupon.userEmail ?? <span className="text-mld-muted-text">&mdash;</span>}</div>
                                        {(coupon.userFirstName || coupon.userLastName) && (
                                            <div className="text-mld-muted-text">
                                                {[coupon.userFirstName, coupon.userLastName].filter(Boolean).join(' ')}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-mld-text-secondary">
                                        {coupon.organizationName ?? <span className="text-mld-muted-text">&mdash;</span>}
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-mld-text-secondary">
                                        {fmtDiscount(coupon)}
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-mld-text-secondary">
                                        {coupon.pointsCost > 0 ? (
                                            coupon.pointsCost
                                        ) : (
                                            <span className="text-mld-muted-text">Free</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <ValidBadge valid={coupon.valid} />
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-mld-muted-text">
                                        {fmtDate(coupon.usedOn)}
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-mld-muted-text">
                                        {fmtDate(coupon.createdAt)}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                type="button"
                                                onClick={() => setEditTarget(coupon)}
                                                className="rounded p-1 text-mld-muted-text hover:bg-mld-menu-background hover:text-mld-text-secondary"
                                                title="Edit">
                                                <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setToggleTarget(coupon)}
                                                className={clsx(
                                                    'rounded p-1',
                                                    coupon.valid
                                                        ? 'text-mld-muted-text hover:bg-mld-danger/5 hover:text-mld-danger'
                                                        : 'text-mld-muted-text hover:bg-mld-status-normal/10 hover:text-mld-status-normal'
                                                )}
                                                title={coupon.valid ? 'Invalidate' : 'Re-activate'}>
                                                {coupon.valid
                                                    ? <Ban className="h-3.5 w-3.5" />
                                                    : <CheckCircle className="h-3.5 w-3.5" />
                                                }
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {total > PAGE_SIZE && (
                <div className="mt-3 flex items-center justify-between text-xs text-mld-muted-text">
                    <span>
                        {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={offset === 0}
                            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                            className="rounded border border-mld-panel-border px-2 py-1 disabled:opacity-40 hover:bg-mld-menu-background">
                            Prev
                        </button>
                        <button
                            type="button"
                            disabled={offset + PAGE_SIZE >= total}
                            onClick={() => setOffset((o) => o + PAGE_SIZE)}
                            className="rounded border border-mld-panel-border px-2 py-1 disabled:opacity-40 hover:bg-mld-menu-background">
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Create / Edit modal */}
            <CouponFormModal
                open={createOpen || editTarget != null}
                coupon={editTarget}
                orgId={orgId}
                onClose={() => {
                    setCreateOpen(false);
                    setEditTarget(null);
                }}
                onSaved={() => {
                    queryClient.invalidateQueries({queryKey: ['admin', 'coupons']});
                    setCreateOpen(false);
                    setEditTarget(null);
                }}
            />

            {/* Activate / Invalidate confirmation */}
            <Transition appear show={toggleTarget != null} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setToggleTarget(null)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-sm rounded-2xl border border-mld-panel-border bg-white shadow-xl">
                                    <div className="px-6 py-5">
                                        <Dialog.Title className="text-base font-semibold text-mld-menu-text">
                                            {toggleTarget?.valid ? 'Invalidate Coupon' : 'Re-activate Coupon'}
                                        </Dialog.Title>
                                        <p className="mt-2 text-sm text-mld-muted-text">
                                            Coupon{' '}
                                            <span className="font-mono font-semibold text-mld-text-primary">
                                                {toggleTarget?.code}
                                            </span>{' '}
                                            will be{' '}
                                            {toggleTarget?.valid
                                                ? 'disabled and can no longer be redeemed.'
                                                : 're-enabled and can be redeemed again.'}
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-end gap-3 border-t border-mld-panel-border px-6 py-4">
                                        <button
                                            type="button"
                                            onClick={() => setToggleTarget(null)}
                                            className="rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-text-secondary hover:bg-mld-menu-background">
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            disabled={toggleMutation.isPending}
                                            onClick={() =>
                                                toggleTarget &&
                                                toggleMutation.mutate({id: toggleTarget.id, valid: !toggleTarget.valid})
                                            }
                                            className={clsx(
                                                'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm text-white disabled:opacity-60',
                                                toggleTarget?.valid
                                                    ? 'bg-mld-danger/50 hover:bg-mld-danger'
                                                    : 'bg-mld-status-normal/70 hover:bg-mld-status-normal'
                                            )}>
                                            {toggleMutation.isPending ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : null}
                                            {toggleTarget?.valid ? 'Invalidate' : 'Re-activate'}
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
};

// ── Create / Edit form modal ──────────────────────────────────────────

interface FormModalProps {
    open: boolean;
    coupon: AdminCoupon | null;
    orgId?: number;
    onClose: () => void;
    onSaved: () => void;
}

const EMPTY_FORM = (): AdminCouponInput => ({
    code: '',
    description: '',
    discountType: 'percentage',
    discountAmount: 10,
    userId: null,
    valid: true,
    expiresAt: null,
    pointsCost: 0,
});

function fromCoupon(c: AdminCoupon): AdminCouponInput {
    return {
        code: c.code,
        description: c.description ?? '',
        discountType: c.discountType,
        discountAmount: c.discountAmount,
        userId: c.userId ?? null,
        valid: c.valid,
        expiresAt: c.expiresAt ? c.expiresAt.slice(0, 10) : null,
        pointsCost: c.pointsCost,
    };
}

const CouponFormModal: React.FC<FormModalProps> = ({open, coupon, onClose, onSaved}) => {
    const isEdit = coupon != null;
    const [form, setForm] = useState<AdminCouponInput>(EMPTY_FORM);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    // Sync form when coupon prop changes
    React.useEffect(() => {
        if (open) {
            setForm(coupon ? fromCoupon(coupon) : EMPTY_FORM());
            setFieldErrors({});
        }
    }, [open, coupon]);

    const set = (key: keyof AdminCouponInput, value: unknown) => {
        setForm((f) => ({...f, [key]: value}));
        setFieldErrors((e) => {
            const next = {...e};
            delete next[key];
            return next;
        });
    };

    const createMutation = useMutation({
        mutationFn: (input: AdminCouponInput) => AdminService.createCoupon(input),
        onSuccess: () => {
            toast.success('Coupon created');
            onSaved();
        },
        onError: (err: any) => {
            const msg = err?.response?.data?.error ?? 'Failed to create coupon';
            toast.error(msg);
        },
    });

    const updateMutation = useMutation({
        mutationFn: (input: Partial<AdminCouponInput>) => AdminService.updateCoupon(coupon!.id, input),
        onSuccess: () => {
            toast.success('Coupon saved');
            onSaved();
        },
        onError: (err: any) => {
            const msg = err?.response?.data?.error ?? 'Failed to save coupon';
            toast.error(msg);
        },
    });

    const isPending = createMutation.isPending || updateMutation.isPending;

    const validate = (): boolean => {
        const errs: Record<string, string> = {};
        if (!form.code.trim()) errs.code = 'Code is required';
        if (!form.discountType) errs.discountType = 'Select a type';
        if (!form.discountAmount || Number(form.discountAmount) <= 0) errs.discountAmount = 'Must be > 0';
        if (form.userId !== null && form.userId !== undefined && Number(form.userId) <= 0) errs.userId = 'Must be a valid user ID';
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        const rawUserId = form.userId != null && String(form.userId).trim() !== '' ? Number(form.userId) : null;
        const payload: AdminCouponInput = {
            ...form,
            code: form.code.trim().toUpperCase(),
            discountAmount: Number(form.discountAmount),
            userId: rawUserId && rawUserId > 0 ? rawUserId : null,
            pointsCost: Number(form.pointsCost) || 0,
            expiresAt: form.expiresAt || null,
            description: form.description || null,
        };
        if (isEdit) {
            updateMutation.mutate(payload);
        } else {
            createMutation.mutate(payload);
        }
    };

    const inputCls = (field: string) =>
        clsx(
            'w-full rounded-md border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary',
            fieldErrors[field] ? 'border-mld-danger' : 'border-gray-300'
        );

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                </Transition.Child>
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center px-4 py-8">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-lg rounded-2xl border border-mld-panel-border bg-white shadow-xl">
                                <div className="border-b border-mld-panel-border px-6 py-4">
                                    <Dialog.Title className="text-lg font-semibold text-mld-menu-text">
                                        {isEdit ? 'Edit Coupon' : 'New Coupon'}
                                    </Dialog.Title>
                                </div>

                                <div className="px-6 py-5 space-y-4">
                                    {/* Code */}
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                            Code <span className="text-mld-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={form.code}
                                            onChange={(e) => set('code', e.target.value.toUpperCase())}
                                            placeholder="SAVE10"
                                            className={inputCls('code')}
                                        />
                                        {fieldErrors.code && (
                                            <p className="mt-1 text-xs text-mld-danger">{fieldErrors.code}</p>
                                        )}
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                            Description
                                        </label>
                                        <input
                                            type="text"
                                            value={form.description ?? ''}
                                            onChange={(e) => set('description', e.target.value)}
                                            placeholder="Optional note"
                                            className={inputCls('description')}
                                        />
                                    </div>

                                    {/* Discount type + amount */}
                                    <div className="flex gap-3">
                                        <div className="w-1/3">
                                            <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                Type <span className="text-mld-danger">*</span>
                                            </label>
                                            <select
                                                value={form.discountType}
                                                onChange={(e) => set('discountType', e.target.value as CouponDiscountType)}
                                                className={inputCls('discountType')}>
                                                {DISCOUNT_TYPES.map((t) => (
                                                    <option key={t} value={t}>
                                                        {t === 'percentage' ? 'Percentage (%)' : 'Dollars ($)'}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex-1">
                                            <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                Amount <span className="text-mld-danger">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                min="0.01"
                                                step="0.01"
                                                value={form.discountAmount}
                                                onChange={(e) => set('discountAmount', e.target.value)}
                                                className={inputCls('discountAmount')}
                                            />
                                            {fieldErrors.discountAmount && (
                                                <p className="mt-1 text-xs text-mld-danger">{fieldErrors.discountAmount}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* User ID */}
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                            User ID{' '}
                                            <span className="font-normal text-mld-muted-text">(optional)</span>
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            step="1"
                                            value={form.userId ?? ''}
                                            onChange={(e) => set('userId', e.target.value === '' ? null : e.target.value)}
                                            placeholder="Leave blank for unassigned"
                                            className={inputCls('userId')}
                                        />
                                        {fieldErrors.userId && (
                                            <p className="mt-1 text-xs text-mld-danger">{fieldErrors.userId}</p>
                                        )}
                                    </div>

                                    {/* Points cost + expires */}
                                    <div className="flex gap-3">
                                        <div className="flex-1">
                                            <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                Points Cost
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                value={form.pointsCost ?? 0}
                                                onChange={(e) => set('pointsCost', e.target.value)}
                                                className={inputCls('pointsCost')}
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                Expires At
                                            </label>
                                            <input
                                                type="date"
                                                value={form.expiresAt ?? ''}
                                                onChange={(e) => set('expiresAt', e.target.value || null)}
                                                className={inputCls('expiresAt')}
                                            />
                                        </div>
                                    </div>

                                </div>

                                <div className="flex items-center justify-end gap-3 border-t border-mld-panel-border px-6 py-4">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-text-secondary hover:bg-mld-menu-background">
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={isPending}
                                        className="inline-flex items-center justify-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-sm text-white hover:bg-mld-primary-700 disabled:opacity-60">
                                        {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                        {isEdit ? 'Save Changes' : 'Create Coupon'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default AdminCouponsManager;
